import type { IndustryContent } from '@/types/content';

export const paintingContent: IndustryContent = {
  slug: 'painting',
  name: 'Painting',
  headline: 'The Operating System for Painting Contractors',
  subheadline:
    'Estimate accurately, manage multi-day projects, and keep crews productive—all from one platform built for painting professionals.',
  heroImageUrl: '/images/industries/painting-hero.webp',
  painPoints: [
    {
      title: 'Estimating is an art (and a gamble)',
      description:
        'Square footage, prep work, paint quality—getting bids right takes experience. Underbid and you lose money. Overbid and you lose the job.',
      icon: 'estimate',
    },
    {
      title: 'Multi-day jobs get messy',
      description:
        'A 3-day interior job needs planning: primer day 1, first coat day 2, finish day 3. Coordinating crews and materials is complex.',
      icon: 'calendar',
    },
    {
      title: 'Material costs fluctuate',
      description:
        'Paint prices change, customers want specific brands, and leftover paint sits in your warehouse. Tracking costs per job is hard.',
      icon: 'materials',
    },
    {
      title: 'Crew productivity varies wildly',
      description:
        'Some painters knock out rooms fast. Others take forever. Without data, you can\'t coach or optimize.',
      icon: 'productivity',
    },
    {
      title: 'Color documentation is chaos',
      description:
        'Customer wants the same color as three years ago. Good luck finding it in your records.',
      icon: 'color',
    },
  ],
  features: [
    {
      title: 'Square Footage Estimating',
      description:
        'Calculate paint quantities, labor hours, and material costs. Build accurate estimates that protect your margins.',
      icon: 'estimate',
    },
    {
      title: 'Project Phase Scheduling',
      description:
        'Break jobs into phases: prep, prime, paint, finish. Schedule the right crew for each phase.',
      icon: 'phases',
    },
    {
      title: 'Material Tracking',
      description:
        'Track paint purchases, usage per job, and inventory. Know your true material costs and avoid waste.',
      icon: 'materials',
    },
    {
      title: 'Crew Time Tracking',
      description:
        'GPS-verified clock-in/out per job. Track productivity by painter and coach your team with data.',
      icon: 'time',
    },
    {
      title: 'Color & Finish Records',
      description:
        'Store color codes, brands, and finishes for every property. Repaint jobs start with perfect information.',
      icon: 'color',
    },
    {
      title: 'Before/After Portfolio',
      description:
        'Required photos on every job build your marketing portfolio automatically. Show off your best work.',
      icon: 'camera',
    },
  ],
  testimonial: {
    id: 'painting-1',
    quote:
      'We finally know which jobs make money and which don\'t. The estimating tool paid for itself in the first month by catching two bids I would have underbid.',
    author: 'Sarah Mitchell',
    role: 'Owner',
    company: 'Mitchell Painting Co.',
    industry: 'painting',
    stats: {
      label: 'Margin Improvement',
      value: '+15%',
    },
  },
  stats: [
    { label: 'Estimating Accuracy', value: '+30%' },
    { label: 'Material Waste', value: '-25%' },
    { label: 'Crew Productivity', value: '+20%' },
    { label: 'Repeat Customers', value: '45%' },
  ],
  seo: {
    title: 'Painting Contractor Software | Estimating & Scheduling | Omniforge',
    description:
      'Painting contractor software for residential and commercial painters. Estimating, project scheduling, material tracking, and crew management. Start your free trial.',
    keywords: [
      'painting contractor software',
      'painting business software',
      'painting estimating software',
      'painter scheduling software',
      'painting company software',
    ],
  },
};
